import { useRef, useState } from 'react'
import { X } from 'lucide-react'
import Button from '../ui/Button.jsx'
import Input from '../ui/Input.jsx'

const TYPE_OPTIONS = [
  { value: 'url', label: 'Web page (URL)' },
  { value: 'page', label: 'Go to book page' },
  { value: 'video', label: 'Video (YouTube / MP4)' },
  { value: 'image', label: 'Image popup (URL)' },
  { value: 'text', label: 'Text note' }
]

/** Drag-to-draw hotspot editor, ported from the vanilla Studio's link editor
 *  (studio/studio.js drawEditorPage) onto the same FlipIntake.addLink/
 *  updateLink/deleteLink primitives, so both editors share identical data
 *  shape and behaviour. */
export default function LinkEditorModal({ open, onClose, pages, onChange, FlipIntake }) {
  const [selIdx, setSelIdx] = useState(0)
  const [drawing, setDrawing] = useState(null) // {x,y,bw,bh}
  const [rect, setRect] = useState(null) // current drag rect in px
  const [form, setForm] = useState(null) // {lk, idx, x, y}
  const stageRef = useRef(null)

  if (!open) return null
  const page = pages[selIdx]

  function stageCoords(e) {
    const b = stageRef.current.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(b.width, e.clientX - b.left)),
      y: Math.max(0, Math.min(b.height, e.clientY - b.top)),
      bw: b.width, bh: b.height
    }
  }

  function handleMouseDown(e) {
    if (e.target.closest('.hs') || e.target.closest('.link-form')) return
    const c = stageCoords(e)
    setDrawing(c)
    setRect({ x: c.x, y: c.y, w: 0, h: 0 })
  }
  function handleMouseMove(e) {
    if (!drawing) return
    const c = stageCoords(e)
    setRect({
      x: Math.min(drawing.x, c.x), y: Math.min(drawing.y, c.y),
      w: Math.abs(c.x - drawing.x), h: Math.abs(c.y - drawing.y)
    })
  }
  function handleMouseUp() {
    if (!drawing || !rect) { setDrawing(null); return }
    const dr = drawing; const r = rect
    setDrawing(null); setRect(null)
    if (r.w < 8 || r.h < 8) return
    setForm({
      lk: { x: r.x / dr.bw, y: r.y / dr.bh, w: r.w / dr.bw, h: r.h / dr.bh, type: 'url', value: '', label: '' },
      idx: -1, x: r.x, y: r.y
    })
  }

  function openExisting(lk, idx, px, py) {
    setForm({ lk, idx, x: px, y: py })
  }

  function handleSave(type, value) {
    const patch = { type, value: value.trim(), label: value.trim() }
    if (!patch.value) return
    if (form.idx < 0) {
      FlipIntake.addLink(page, { ...form.lk, ...patch })
    } else {
      FlipIntake.updateLink(page, form.idx, patch)
    }
    setForm(null)
    onChange()
  }
  function handleDelete() {
    FlipIntake.deleteLink(page, form.idx)
    setForm(null)
    onChange()
  }

  return (
    <div className="fixed inset-0 z-[70] bg-navy2/95 backdrop-blur-md flex flex-col">
      <div className="flex items-center gap-4 px-5 py-3.5 border-b border-line">
        <h3 className="font-display font-bold text-[14px]">Interactive Links &amp; Hotspots</h3>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onClose}>Done</Button>
      </div>

      <div className="flex-1 grid grid-cols-[160px_1fr] min-h-0">
        <div className="overflow-y-auto border-r border-line p-3 space-y-2">
          {pages.map((p, i) => (
            <div
              key={i}
              onClick={() => { setSelIdx(i); setForm(null) }}
              className={`relative rounded-md overflow-hidden cursor-pointer border-2 ${i === selIdx ? 'border-blue' : 'border-transparent'}`}
            >
              <img src={p.thumbUrl} className="w-full block" />
              <span className="absolute top-0.5 left-1 text-[10px] bg-black/60 text-white px-1.5 rounded-full">{i + 1}</span>
              {p.links?.length > 0 && (
                <b className="absolute bottom-0.5 right-1 text-[9px] bg-blue text-white px-1.5 rounded-full">{p.links.length}</b>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center p-5 overflow-auto">
          {page && (
            <div
              ref={stageRef}
              className="relative shadow-elev3 cursor-crosshair select-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            >
              <img src={page.url} draggable={false} className="block max-w-full" style={{ maxHeight: '72vh' }} />

              {(page.links || []).map((lk, idx) => (
                <div
                  key={idx}
                  className="hs absolute border-2 border-sky bg-sky/20 hover:bg-sky/30 cursor-pointer"
                  style={{ left: lk.x * 100 + '%', top: lk.y * 100 + '%', width: lk.w * 100 + '%', height: lk.h * 100 + '%' }}
                  onClick={(e) => { e.stopPropagation(); openExisting(lk, idx, e.nativeEvent.offsetX, e.nativeEvent.offsetY) }}
                >
                  <b className="absolute -top-2.5 left-0 text-[9px] bg-sky text-[#04152e] px-1 rounded-sm whitespace-nowrap">{lk.type || 'link'}</b>
                </div>
              ))}

              {rect && <div className="absolute border-2 border-blue bg-blue/20 pointer-events-none" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />}

              {form && <LinkForm form={form} onSave={handleSave} onDelete={form.idx >= 0 ? handleDelete : null} onCancel={() => setForm(null)} />}
            </div>
          )}
        </div>
      </div>

      <div className="text-muted text-[12px] px-5 py-2.5 border-t border-line">
        Drag on the page to draw a clickable area, then choose what it links to. Click an existing hotspot to edit or delete it.
      </div>
    </div>
  )
}

function LinkForm({ form, onSave, onDelete, onCancel }) {
  const [type, setType] = useState(form.lk.type || 'url')
  const [value, setValue] = useState(form.lk.value || '')
  const placeholder = type === 'page' ? 'e.g. 5' : type === 'text' ? 'Short note…' : type === 'video' ? 'https://youtu.be/…' : 'https://…'

  return (
    <div
      className="link-form absolute z-10 bg-panel border border-line rounded-md p-3 w-[250px] shadow-elev3"
      style={{ left: Math.max(0, form.x), top: form.y + 10 }}
      onClick={(e) => e.stopPropagation()}
    >
      <label className="block text-[10px] uppercase tracking-wide text-muted mb-1">Link type</label>
      <select value={type} onChange={(e) => setType(e.target.value)} className="w-full h-9 mb-2 bg-panel2 border border-line rounded-sm px-2 text-[13px]">
        {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <label className="block text-[10px] uppercase tracking-wide text-muted mb-1">{type === 'page' ? 'Page number' : type === 'text' ? 'Note text' : 'URL'}</label>
      <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} className="mb-2 h-9" />
      <div className="flex gap-2">
        {onDelete && <Button variant="danger" size="sm" onClick={onDelete} className="flex-1">Delete</Button>}
        <Button variant="ghost" size="sm" onClick={onCancel} className="flex-1">Cancel</Button>
        <Button size="sm" onClick={() => onSave(type, value)} className="flex-1">Save</Button>
      </div>
    </div>
  )
}
